import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import SoftBox from 'components/SoftBox';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import MiniStatisticsCard from 'examples/Cards/StatisticsCards/MiniStatisticsCard';
import Default1 from './Graph1/Default1';
import CustomdateD from './CustomdateD';
import './Style.css';
import { CircularProgress, Box, Typography, Button } from '@mui/material';
import Social from './Graph3/Social';
import Default2 from './Graph2/Default2';
import Default3 from './Graph4/Default3';
import { API_BASE_URL, FAST_API_BASE_URL, LAMBDA_BASE_URL } from '../../../constants';
import { useQueryParams } from 'context/QueryParamsContext';
import { useDomain } from 'context/DomainContext';
import { useSites } from 'context/SitesContext';
import { useAuth } from 'context/AuthContext';
import encryptedStorage from 'utils/encryptedStorage';

function Dashboard() {
  const { dateRange, setDateRange } = useQueryParams();
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalVisits, setTotalVisits] = useState(0);
  const [totalSearches, setTotalSearches] = useState(0);
  const [totalLikes, setTotalLikes] = useState(0);
  const [totalDislikes, setTotalDislikes] = useState(0);
  const [loading, setLoading] = useState(true);
  const [totalEmails, setTotalEmails] = useState(0);
  const [totalCopies, setTotalCopies] = useState(0);
  const [totalContacts, setTotalContacts] = useState(0);
  const [uniqueUsers, setUniqueUsers] = useState(0);
  const [isAuthorized, setIsAuthorized] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [validationLoading, setValidationLoading] = useState(true);
  const [checkingDomainUsers, setCheckingDomainUsers] = useState(false);
  const [domainUsersCount, setDomainUsersCount] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const validationAttemptsRef = useRef(0);
  const maxAttempts = 3;

  const { currentDomain, isAuthenticated, user } = useAuth();

  const getDomainIdFromQuery = useCallback(() => {
    const queryParams = new URLSearchParams(location.search);
    const domainId = queryParams.get('domain_id');
    // console.log('getDomainIdFromQuery called, domain_id:', domainId);
    return domainId;
  }, [location.search]);

  const domainIdFromQuery = getDomainIdFromQuery();

  useEffect(() => {
    if (domainIdFromQuery) {
      // console.log('Saving domain_id to encrypted storage:', domainIdFromQuery);
      try {
        encryptedStorage.setItem('url_domain_id', domainIdFromQuery);
      } catch (error) {
        console.error('Failed to save domain_id to encrypted storage:', error);
      }
    }
  }, [domainIdFromQuery]);

  const getAllDomainTokens = useCallback(() => {
    const tokens = [];
    
    if (domainIdFromQuery) {
      // console.log('Found domain_id in query params:', domainIdFromQuery);
      tokens.push({ 
        token: domainIdFromQuery.trim(), 
        source: 'query_params',
        priority: 1 
      });
    }
    
    try {
      const savedDomainId = encryptedStorage.getItem('domain_token');
      if (savedDomainId && !tokens.some(t => t.token === savedDomainId)) {
        tokens.push({ 
          token: savedDomainId, 
          source: 'url_storage',
          priority: 1.5
        });
      }
    } catch (error) {
      // console.log('Error reading url_domain_id from storage:', error);
    }
    
    if (currentDomain?.domain_token) {
      tokens.push({ 
        token: currentDomain.domain_token, 
        source: 'current_domain',
        priority: 2 
      });
    }
    
    if (user?.domains && user.domains.length > 0) {
      user.domains.forEach((domain, index) => {
        const token = domain.domain_token || domain.domain_id;
        if (token && !tokens.some(t => t.token === token)) {
          tokens.push({ 
            token, 
            source: `user_domain_${index}`,
            priority: 3 
          });
        }
      });
    }
    
    try {
      const userData = encryptedStorage.getItem('user');
      const domains = userData?.domains || [];
      domains.forEach((domain, index) => {
        const token = domain.domain_token || domain.domain_id;
        if (token && !tokens.some(t => t.token === token)) {
          tokens.push({ 
            token, 
            source: `storage_domain_${index}`,
            priority: 4 
          });
        }
      });
      
      const storedDomainToken = encryptedStorage.getItem('domain_token');
      if (storedDomainToken && !tokens.some(t => t.token === storedDomainToken)) {
        tokens.push({ 
          token: storedDomainToken, 
          source: 'encrypted_storage',
          priority: 5 
        });
      }
    } catch (error) {
      // console.log('Error reading encrypted storage:', error);
    }
    
    tokens.sort((a, b) => a.priority - b.priority);
    
    // console.log('Available domain tokens (sorted):', tokens);
    return tokens;
  }, [domainIdFromQuery, currentDomain, user]);

  const saveDomainTokenToStorage = useCallback((token) => {
    try {
      encryptedStorage.setItem('domain_token', token);
      return true;
    } catch (error) {
      console.error('Failed to save domain token to encrypted storage:', error);
      return false;
    }
  }, []);

  const getSecretFromStorage = useCallback(() => {
    try {
      return localStorage.getItem('secret');
    } catch (error) {
      console.error('Failed to get secret from encrypted storage:', error);
      return null;
    }
  }, []);

  const buildQueryString = useCallback(() => {
    const params = new URLSearchParams(location.search);
    const domainId = params.get('domain_id');
    
    const newParams = new URLSearchParams();
    
    if (domainId) {
      newParams.set('domain_id', domainId);
    }
    if (dateRange.start_date) newParams.set('start_date', dateRange.start_date);
    if (dateRange.end_date) newParams.set('end_date', dateRange.end_date);
    
    return `?${newParams.toString()}`;
  }, [location.search, dateRange]);

  const queryString = buildQueryString();

  useEffect(() => {
    const postHeight = () => {
      const height = document.documentElement.scrollHeight;
      window.parent.postMessage({ iframeHeight: height }, '*');
    };
    
    const observer = new ResizeObserver(postHeight);
    observer.observe(document.body);
    observer.observe(document.documentElement);
    
    const interval = setInterval(postHeight, 1000);
    setTimeout(postHeight, 100);
    postHeight();
    
    return () => {
      observer.disconnect();
      clearInterval(interval);
    };
  }, []);

  const checkDomainUsersCount = useCallback(async (domainToken) => {
    if (!domainToken) return null;
    
    setCheckingDomainUsers(true);
    try {
      const res = await axios.get(
        `${LAMBDA_BASE_URL}/domain-users-count`,
        {
          headers: {
            "DOMAIN-TOKEN": domainToken,
          }
        }
      );
      
      if (res.data?.messages?.count !== undefined) {
        setDomainUsersCount(res.data.messages.count);
        return res.data.messages.count;
      }
      return null;
    } catch (error) {
      console.error("Failed to fetch domain users count:", error);
      return null;
    } finally {
      setCheckingDomainUsers(false);
    }
  }, []);

  const openAuthDialogBasedOnUsers = useCallback((count) => {
    if (count === 0) {
      // console.log('No users found for domain, opening signup dialog');
      window.dispatchEvent(new CustomEvent('open-signup-dialog', { 
        detail: { domainToken: domainIdFromQuery } 
      }));
    } else if (count > 0) {
      // console.log('Users found for domain, opening login dialog');
      window.dispatchEvent(new CustomEvent('open-login-dialog', { 
        detail: { domainToken: domainIdFromQuery } 
      }));
    }
  }, [domainIdFromQuery]);

  const validateToken = async (attempt = 1) => {
    setRetryCount(attempt);
    validationAttemptsRef.current = attempt;
    
    if (attempt > maxAttempts) {
      // console.log('Max validation attempts reached');
      setIsAuthorized(false);
      setValidationLoading(false);
      if (domainIdFromQuery && !isAuthenticated()) {
        // console.log('Checking domain users count for automatic form display');
        const usersCount = await checkDomainUsersCount(domainIdFromQuery);
        if (usersCount !== null) {
          openAuthDialogBasedOnUsers(usersCount);
        }
      }
      
      return;
    }

    const allTokens = getAllDomainTokens();
    const secret = getSecretFromStorage();
    const queryParamToken = allTokens.find(t => t.source.includes('query'));
    
    if (queryParamToken) {
      // console.log(`Attempt ${attempt}: Validating query param token:`, queryParamToken.token);
      try {
        const result = await axios.get(`${LAMBDA_BASE_URL}/validate-token`, {
          headers: { 'domain-token': queryParamToken.token },
          timeout: 5000
        });
        
        if (result.status === 200) {
          // console.log('Query param token validation successful');
          saveDomainTokenToStorage(queryParamToken.token);
          setIsAuthorized(true);
          setValidationLoading(false);
          return;
        }
      } catch (error) {
        // console.log('Query param token validation failed:', error.message);
      }
    }

    if (secret) {
      try {
        const result = await axios.get(`${LAMBDA_BASE_URL}/validate-token`, {
          headers: { 'DOMAIN-TOKEN': currentDomain.domain_token },
          timeout: 5000
        });
        
        if (result.status === 200) {
          // console.log('Secret validation successful');
          setIsAuthorized(true);
          setValidationLoading(false);
          return;
        }
      } catch (error) {
        // console.log('Secret validation failed:', error.message);
      }
    }

    for (const tokenInfo of allTokens) {
      if (tokenInfo.source.includes('query')) {
        continue; 
      }
      
      try {
        const result = await axios.get(`${LAMBDA_BASE_URL}/validate-token`, {
          headers: { 'domain-token': tokenInfo.token },
          timeout: 5000
        });
        
        if (result.status === 200) {
          // console.log(`Validation successful with token from ${tokenInfo.source}`);
          saveDomainTokenToStorage(tokenInfo.token);
          setIsAuthorized(true);
          setValidationLoading(false);
          return;
        }
      } catch (error) {
        // console.log(`Token validation failed for ${tokenInfo.source}:`, error.message);
      }
    }

    // console.log(`All validations failed on attempt ${attempt}. Retrying...`);
    const delay = Math.min(attempt * 1000, 5000);
    setTimeout(() => validateToken(attempt + 1), delay);
  };

  useEffect(() => {
    // console.log('Dashboard mounted, starting validation');
    // console.log('URL Search params:', location.search);
    // console.log('Domain ID from query:', domainIdFromQuery);
    
    setIsAuthorized(null);
    setValidationLoading(true);
    validationAttemptsRef.current = 0;
    
    const timer = setTimeout(() => {
      validateToken(1);
    }, 100);
    
    return () => clearTimeout(timer);
  }, [location.search, domainIdFromQuery]);

  useEffect(() => {
    const handleAuthStateChanged = () => {
      // console.log('Auth state changed, re-validating...');
      setIsAuthorized(null);
      setValidationLoading(true);
      validationAttemptsRef.current = 0;
      validateToken(1);
    };

    window.addEventListener('auth-state-changed', handleAuthStateChanged);

    return () => {
      window.removeEventListener('auth-state-changed', handleAuthStateChanged);
    };
  }, [validateToken]);

  const fetchAnalytics = async () => {
    if (!isAuthorized || !dateRange.start_date || !dateRange.end_date) return;
    
    setLoading(true);
    try {
      const { start_date, end_date } = dateRange;
      
      const allTokens = getAllDomainTokens();
      const secret = getSecretFromStorage();
      
      let headers = {};
      if (secret) {
        headers['SECRET-TOKEN'] = secret;
      } else if (allTokens.length > 0) {
        headers['DOMAIN-TOKEN'] = allTokens[0].token;
      }

      const res = await axios.get(`${FAST_API_BASE_URL}/api/dashboard-analytics`, {
        params: { start_date, end_date },
        headers:{
          'DOMAIN-TOKEN': currentDomain.domain_token,
        },
      });
      const data = res.data;
      setTotalUsers(data.result.total_users_count);
      setTotalVisits(data.result.total_visit_count);
      setTotalSearches(data.result.total_searches_count);
      setTotalLikes(data.result.total_likes_count);
      setTotalDislikes(data.result.total_dislikes_count);
      setTotalEmails(data.result.total_email_sent_count);
      setTotalCopies(data.result.total_copy_counts);
      setTotalContacts(data.result.total_contactus_count);
    } catch (error) {
      console.error('Error fetching analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    if (!isAuthorized || !dateRange.start_date || !dateRange.end_date) return;
    
    try {
      const { start_date, end_date } = dateRange;
      const allTokens = getAllDomainTokens();
      const secret = getSecretFromStorage();
      
      let headers = {};
      if (secret) {
        headers['SECRET-TOKEN'] = secret;
      } else if (allTokens.length > 0) {
        headers['DOMAIN-TOKEN'] = allTokens[0].token;
      }
      
      const res = await axios.get(`${LAMBDA_BASE_URL}/unique-users`, {
        params: { start_date, end_date },
        headers,
      });
      const data1 = res.data;
      setUniqueUsers(data1.result[0].count);
    } catch (error) {
      // console.log('error fetching analytics data:', error);
    }
  };

  useEffect(() => {
    if (dateRange.start_date && dateRange.end_date && isAuthorized) {
      fetchAnalytics();
      fetchUsers();
    }
  }, [dateRange, isAuthorized, getAllDomainTokens, getSecretFromStorage]);

  const handleDateRangeSelect = ({ start, end }) => {
    setDateRange({ start_date: start, end_date: end });
    
    try {
      encryptedStorage.setItem('saved_start_date', start);
      encryptedStorage.setItem('saved_end_date', end);
    } catch (error) {
      console.error('Failed to save date range to encrypted storage:', error);
    }
    
    const queryParams = new URLSearchParams(location.search);
    const domainId = queryParams.get('domain_id');
    
    const newParams = new URLSearchParams();
    
    if (domainId) {
      newParams.set('domain_id', domainId);
    }
    
    newParams.set('start_date', start);
    newParams.set('end_date', end);
    
    navigate({ pathname: location.pathname, search: newParams.toString() });
  };

  if (validationLoading || isAuthorized === null) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="500px" flexDirection="column">
          <CircularProgress color='teal' />
        </Box>
      </DashboardLayout>
    );
  }

  if (isAuthorized === false) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="500px" flexDirection="column">
          <Typography variant="h5" sx={{ mb: 2, color: '#219297' }}>
            Access Required
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 4, textAlign: 'center', maxWidth: 500 }}>
            {domainIdFromQuery 
              ? `You need to log in to access analytics for this domain.`
              : `Please log in or sign up to access the dashboard.`}
          </Typography>
          
          {domainIdFromQuery && (
            <Box sx={{ 
              p: 2, 
              mb: 4, 
              backgroundColor: '#f0fdfa', 
              borderRadius: 2,
              border: '1px solid #ccfbf1'
            }}>
              <Typography variant="body2" color="text.secondary">
                Domain ID: <strong>{domainIdFromQuery}</strong>
              </Typography>
            </Box>
          )}
          
          <Box display="flex" gap={2} flexWrap="wrap" justifyContent="center">
            <Button 
              onClick={() => window.dispatchEvent(new CustomEvent('open-login-dialog'))}
              variant="contained"
              sx={{
                padding: '12px 24px',
                backgroundColor: '#219297',
                color: 'white',
                borderRadius: '8px',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#1a7a7d'
                }
              }}
            >
              Login to Access
            </Button>
            
            <Button 
              onClick={() => window.dispatchEvent(new CustomEvent('open-signup-dialog'))}
              variant="outlined"
              sx={{
                padding: '12px 24px',
                color: '#219297',
                borderColor: '#219297',
                borderRadius: '8px',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#f0fdfa',
                  borderColor: '#1a7a7d'
                }
              }}
            >
              Create Account
            </Button>
          </Box>
          
          <Box mt={4}>
            <Button 
              onClick={() => {
                try {
                  encryptedStorage.removeItem('domain_token');
                  encryptedStorage.removeItem('secret');
                } catch (error) {
                  console.error('Failed to clear tokens:', error);
                }
                setIsAuthorized(null);
                setValidationLoading(true);
                validateToken(1);
              }}
              variant="text"
              sx={{
                color: '#666',
                textTransform: 'none'
              }}
            >
              Retry validation
            </Button>
          </Box>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox display="flex" justifyContent="space-between" alignItems="center" marginLeft="10px" marginTop="10px" p={0}>
        <CustomdateD onRangeSelect={handleDateRangeSelect} />
      </SoftBox>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="500px" color="info">
          <CircularProgress color='teal' />
        </Box>
      ) : (
        <SoftBox py={3}>
          <SoftBox mb={3}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} lg={4}>
                <Link to={`/visitors${queryString}`}>
                  <MiniStatisticsCard title={{ text: 'Total Visitors / Visits', fontWeight: 'medium' }} count1={`${totalUsers} / ${totalVisits}`} icon={{ color: 'dark', component: 'groups' }} />
                </Link>
              </Grid>
              <Grid item xs={12} sm={6} lg={4}>
                <Link to={`/queries${queryString}`}>
                  <MiniStatisticsCard title={{ text: 'Total Queries', fontWeight: 'medium' }} count={totalSearches} icon={{ color: 'dark', component: 'search' }} />
                </Link>
              </Grid>
              <Grid item xs={12} sm={6} lg={4}>
                <Link to={`/likes${queryString}`}>
                  <MiniStatisticsCard title={{ text: 'Total Likes', fontWeight: 'medium' }} count={totalLikes} icon={{ color: 'dark', component: 'thumb_up' }} />
                </Link>
              </Grid>
              <Grid item xs={12} sm={6} lg={4}>
                <Link to={`/dislikes${queryString}`}>
                  <MiniStatisticsCard title={{ text: 'Total Dislikes', fontWeight: 'medium' }} count={totalDislikes} icon={{ color: 'dark', component: 'thumb_down' }} />
                </Link>
              </Grid>
              <Grid item xs={12} sm={6} lg={4}>
                <Link to={`/emails-sent${queryString}`}>
                <MiniStatisticsCard title={{ text: 'Emails Sent', fontWeight: 'medium' }} count={totalEmails || 0} icon={{ color: 'dark', component: 'email' }} />
                </Link>
              </Grid>
              <Grid item xs={12} sm={6} lg={4}>
                <Link to={`/contact-us${queryString}`}>
                <MiniStatisticsCard title={{ text: 'Contact Us Actions', fontWeight: 'medium' }} count={totalContacts || 0} icon={{ color: 'dark', component: 'perm_contact_calendar_icon' }} />
                </Link>
              </Grid>
            </Grid>
          </SoftBox>
          <Grid container spacing={2}>
            <Grid item xs={12} lg={6}>
              <Default1 />
            </Grid>
            <Grid item xs={12} lg={6}>
              <Default2 />
            </Grid>
            <Grid item xs={12} lg={6}>
              <Social dateRange={dateRange} />
            </Grid>
            <Grid item xs={12} lg={6}>
              <Default3 />
            </Grid>
          </Grid>
        </SoftBox>
      )}
    </DashboardLayout>
  );
}

export default Dashboard;